'use client'

import { GoogleTagManager } from '@next/third-parties/google'
import { useEffect } from 'react'
import { GTM_ID, APP_PRODUCT, type AppName } from '../constants'
import { initAnalytics } from '../gtm'

interface AnalyticsProviderProps {
  /** App identifier — used as `app_origin` in all events and to resolve the GTM container ID */
  appName: AppName | (string & {})
  /** Override the default GTM container ID for this app */
  gtmId?: string
  children?: React.ReactNode
  /** Enable console logging of dataLayer pushes (auto-enabled in non-production) */
  debug?: boolean
}

/**
 * Unified analytics provider. Drop this into any app's root layout to get:
 * - GTM script injection (production only, loads after hydration)
 * - GTM noscript iframe fallback
 * - Automatic `app_origin` and `product` context for GTM and tracked events
 * - SPA page-view tracking via PageViewTracker (import separately)
 *
 * @example
 * ```tsx
 * // apps/rental-marketplace/src/app/layout.tsx
 * import { AnalyticsProvider } from '@repo/analytics'
 *
 * export default function RootLayout({ children }) {
 *   return (
 *     <html>
 *       <body>
 *         <AnalyticsProvider appName="rental-marketplace">
 *           {children}
 *         </AnalyticsProvider>
 *       </body>
 *     </html>
 *   )
 * }
 * ```
 */
export function AnalyticsProvider({
  appName,
  gtmId,
  children,
  debug = false,
}: AnalyticsProviderProps) {
  const appEnvironment =
    process.env.NEXT_PUBLIC_APP_ENVIRONMENT?.trim().toLowerCase() ||
    process.env.NEXT_PUBLIC_ENVIRONMENTAL_TYPE?.trim().toLowerCase()
  const isProd =
    appEnvironment === 'production' || appEnvironment === 'prod'
  const resolvedGtmId =
    gtmId ||
    GTM_ID ||
    process.env.NEXT_PUBLIC_GTM_ID
  const product = APP_PRODUCT[appName as AppName] ?? 'unknown'

  useEffect(() => {
    initAnalytics(appName, product, debug)
  }, [appName, product, debug, isProd])

  // In non-production or when no GTM ID — still init dataLayer for debugging
  if (!isProd || !resolvedGtmId) {
    return <>{children}</>
  }

  return (
    <>
      <GoogleTagManager
        gtmId={resolvedGtmId}
        dataLayer={{ app_origin: appName, product }}
      />
      {children}
    </>
  )
}
